import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Megaphone, Building2, Send, Loader2, CheckCircle2, AlertTriangle,
  Info, Trash2, Eye, Users, Globe, Search, History, X, MapPin, Map, Bell, Phone,
  Sparkles, Smartphone, MailOpen, MousePointerClick,
} from 'lucide-react';
import AutoPilotPanel from '@/components/admin/AutoPilotPanel';
import ExternalSignalsPanel from '@/components/admin/ExternalSignalsPanel';

const SEVERITY_META = {
  info:     { label: 'News',   icon: Info,          color: 'bg-blue-100 text-blue-800 border-blue-300' },
  warning:  { label: 'Update', icon: AlertTriangle, color: 'bg-amber-100 text-amber-800 border-amber-300' },
  critical: { label: 'Urgent', icon: AlertTriangle, color: 'bg-red-100 text-red-800 border-red-300' },
};

const TARGET_ROLES = [
  { value: 'academy_owner', label: 'Academy Owners' },
  { value: 'coach',         label: 'Coaches' },
  { value: 'employee',      label: 'Employees' },
  { value: 'student',       label: 'Students' },
  { value: 'all',           label: 'Everyone (all roles)' },
];

export default function AdminAnnounce() {
  const [academies, setAcademies] = useState([]);
  const [geoOptions, setGeoOptions] = useState({ cities: [], states: [], total_academies: 0 });
  const [loadingAcademies, setLoadingAcademies] = useState(true);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  // Phase 72 — Targeting mode: 'academies' | 'cities' | 'states' | 'all'
  const [targetMode, setTargetMode] = useState('academies');
  const [selectedAcademyIds, setSelectedAcademyIds] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [academySearch, setAcademySearch] = useState('');
  // Phase 72 — Multi-channel toggles
  const [channelInApp, setChannelInApp] = useState(true);
  const [channelWhatsapp, setChannelWhatsapp] = useState(false);

  const [targetRole, setTargetRole] = useState('academy_owner');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('info');
  const [submitting, setSubmitting] = useState(false);
  const [resultDialog, setResultDialog] = useState(null);

  // Broadcast Composer add-ons (AI Copilot + reach estimate + telemetry)
  const [aiDrafting, setAiDrafting] = useState(false);
  const [aiTopicHint, setAiTopicHint] = useState('');
  const [aiSource, setAiSource] = useState(null); // 'llm' | 'fallback_template' | null
  const [reach, setReach] = useState(null);
  const [reachLoading, setReachLoading] = useState(false);
  const [telemetry, setTelemetry] = useState(null);

  useEffect(() => {
    fetchAcademies();
    fetchGeoOptions();
    fetchHistory();
  }, []);

  const fetchAcademies = async () => {
    setLoadingAcademies(true);
    try {
      // The list endpoint lives at /admin/academies (returns an array directly).
      // The /super-admin/* namespace only owns per-academy management routes
      // (tier, delete, resend-welcome, geo-options) — there is no list endpoint there.
      const res = await api.get('/admin/academies');
      const list = res.data?.academies || res.data || [];
      setAcademies(Array.isArray(list) ? list : []);
    } catch (err) {
      toast.error('Failed to load academies');
    } finally {
      setLoadingAcademies(false);
    }
  };

  const fetchGeoOptions = async () => {
    try {
      const res = await api.get('/super-admin/academies/geo-options');
      setGeoOptions(res.data || { cities: [], states: [], total_academies: 0 });
    } catch (err) {
      console.error('geo-options fetch failed', err);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await api.get('/super-admin/announcements', { params: { limit: 30 } });
      setHistory(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('history fetch failed', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const filteredAcademies = useMemo(() => {
    const q = academySearch.trim().toLowerCase();
    if (!q) return academies;
    return academies.filter(a =>
      (a.name || '').toLowerCase().includes(q) ||
      (a.custom_id || '').toLowerCase().includes(q) ||
      (a.city || '').toLowerCase().includes(q)
    );
  }, [academies, academySearch]);

  const toggleAcademy = (id) => {
    setSelectedAcademyIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  const toggleCity = (c) => {
    setSelectedCities((prev) => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  };
  const toggleState = (s) => {
    setSelectedStates((prev) => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  };

  const selectionLabel = useMemo(() => {
    if (targetMode === 'all') return `All ${geoOptions.total_academies || academies.length} academies`;
    if (targetMode === 'academies') {
      if (selectedAcademyIds.length === 0) return 'No academies selected';
      if (selectedAcademyIds.length === 1) {
        const a = academies.find(x => x.id === selectedAcademyIds[0]);
        return a ? `1 academy — ${a.name}` : '1 academy';
      }
      return `${selectedAcademyIds.length} academies`;
    }
    if (targetMode === 'cities') {
      if (selectedCities.length === 0) return 'No cities selected';
      return `${selectedCities.length} ${selectedCities.length === 1 ? 'city' : 'cities'} — ${selectedCities.join(', ')}`;
    }
    if (targetMode === 'states') {
      if (selectedStates.length === 0) return 'No states selected';
      return `${selectedStates.length} ${selectedStates.length === 1 ? 'state' : 'states'} — ${selectedStates.join(', ')}`;
    }
    return '';
  }, [targetMode, selectedAcademyIds, selectedCities, selectedStates, academies, geoOptions]);

  const hasValidTarget = (
    (targetMode === 'all') ||
    (targetMode === 'academies' && selectedAcademyIds.length > 0) ||
    (targetMode === 'cities'    && selectedCities.length > 0) ||
    (targetMode === 'states'    && selectedStates.length > 0)
  );
  const hasValidChannel = channelInApp || channelWhatsapp;
  const canSubmit = !!message.trim() && hasValidTarget && hasValidChannel;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const channels = [];
      if (channelInApp) channels.push('inapp');
      if (channelWhatsapp) channels.push('whatsapp');
      const payload = {
        target_role: targetRole,
        title: title.trim(),
        message: message.trim(),
        severity,
        type: 'announcement',
        channels,
      };
      if (targetMode === 'all') payload.academy_ids = ['all'];
      else if (targetMode === 'academies') payload.academy_ids = selectedAcademyIds;
      else if (targetMode === 'cities') payload.cities = selectedCities;
      else if (targetMode === 'states') payload.states = selectedStates;

      const res = await api.post('/super-admin/announcements', payload);
      toast.success(res.data.message);
      setResultDialog(res.data);
      // Reset form
      setTitle('');
      setMessage('');
      setSelectedAcademyIds([]);
      setSelectedCities([]);
      setSelectedStates([]);
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to broadcast');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this broadcast AND retract every notification it sent? This cannot be undone.')) return;
    try {
      const res = await api.delete(`/super-admin/announcements/${id}`);
      toast.success(res.data.message);
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  // ── AI Copilot draft ────────────────────────────────────────────────────────
  const handleAiDraft = async () => {
    setAiDrafting(true);
    try {
      const res = await api.post('/admin/announce/ai-draft', {
        target_mode: targetMode,
        target_role: targetRole,
        severity,
        topic_hint: aiTopicHint.trim() || undefined,
      });
      if (res.data?.title) setTitle(res.data.title);
      if (res.data?.message) setMessage(res.data.message);
      setAiSource(res.data?.source || null);
      toast.success(res.data?.source === 'llm'
        ? 'AI Copilot drafted a fresh broadcast'
        : 'Drafted from on-device template (LLM unavailable)');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI draft failed');
    } finally {
      setAiDrafting(false);
    }
  };

  // ── Predictive Reach Estimator ──────────────────────────────────────────────
  useEffect(() => {
    if (!hasValidTarget) { setReach(null); return; }
    const ctrl = new AbortController();
    setReachLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.post('/admin/announce/reach-estimate', {
          target_mode: targetMode,
          target_role: targetRole,
          academy_ids: selectedAcademyIds,
          cities: selectedCities,
          states: selectedStates,
        }, { signal: ctrl.signal });
        setReach(res.data);
      } catch (err) {
        if (!ctrl.signal.aborted) setReach(null);
      } finally {
        if (!ctrl.signal.aborted) setReachLoading(false);
      }
    }, 280);
    return () => { ctrl.abort(); clearTimeout(t); };
  }, [targetMode, targetRole, selectedAcademyIds, selectedCities, selectedStates, hasValidTarget]);

  // ── Telemetry summary (Phase 168) ───────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/admin/announce/telemetry-summary?days=7');
        setTelemetry(r.data);
      } catch {
        setTelemetry(null);
      }
    })();
  }, []);

  const SevIcon = SEVERITY_META[severity].icon;

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="admin-announce-page">
      <div className="flex items-center gap-3">
        <div className="p-2.5 bg-primary/10 rounded-lg"><Megaphone className="h-5 w-5 text-primary" /></div>
        <div>
          <h1 className="text-2xl font-bold">Broadcast Composer</h1>
          <p className="text-sm text-muted-foreground">Push messages with surgical precision — individual academies, cities, states, or platform-wide.</p>
        </div>
      </div>

      {/* Phase 169 — Auto-Pilot one-click emergency dispatch */}
      <AutoPilotPanel onDispatch={() => fetchHistory()} />

      {/* Phase 170 — External Signal Auto-Trigger (IMD scrapers, monitoring partners) */}
      <ExternalSignalsPanel />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Composer (2/3 width) */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Send className="h-4 w-4 text-primary" /> Compose New Broadcast
            </CardTitle>
            <CardDescription>Pick how you want to slice the tenant base, then write your message.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Targeting mode tabs */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold">1. Targeting</Label>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs" data-testid="announce-selection-label">
                    <Users className="h-3 w-3 mr-1" /> {selectionLabel}
                  </Badge>
                  {hasValidTarget && (
                    <Badge
                      className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-300 gap-1"
                      data-testid="announce-reach-estimate"
                    >
                      {reachLoading ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Send className="h-3 w-3" />
                      )}
                      {reach
                        ? `~${reach.users} ${reach.users === 1 ? 'recipient' : 'recipients'} · ${reach.academies} ${reach.academies === 1 ? 'academy' : 'academies'}`
                        : 'Estimating reach…'}
                    </Badge>
                  )}
                </div>
              </div>
              <Tabs value={targetMode} onValueChange={setTargetMode}>
                <TabsList className="grid grid-cols-4 w-full">
                  <TabsTrigger value="academies" data-testid="announce-mode-academies"><Building2 className="h-3.5 w-3.5 mr-1" /> Academy</TabsTrigger>
                  <TabsTrigger value="cities" data-testid="announce-mode-cities"><MapPin className="h-3.5 w-3.5 mr-1" /> City</TabsTrigger>
                  <TabsTrigger value="states" data-testid="announce-mode-states"><Map className="h-3.5 w-3.5 mr-1" /> State</TabsTrigger>
                  <TabsTrigger value="all" data-testid="announce-mode-all"><Globe className="h-3.5 w-3.5 mr-1" /> All</TabsTrigger>
                </TabsList>

                {/* Academy multi-select */}
                <TabsContent value="academies" className="mt-3 space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Search by name, custom ID, or city..." value={academySearch} onChange={(e) => setAcademySearch(e.target.value)} className="pl-9 h-9 text-sm" data-testid="announce-academy-search" />
                  </div>
                  <ScrollArea className="h-40 rounded-md border p-2" data-testid="announce-academy-list">
                    {loadingAcademies ? <div className="text-center text-xs text-muted-foreground py-6">Loading academies...</div>
                    : filteredAcademies.length === 0 ? <div className="text-center text-xs text-muted-foreground py-6">No academies match</div>
                    : filteredAcademies.map(a => (
                      <button key={a.id} onClick={() => toggleAcademy(a.id)} className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-xs transition-colors duration-150 ${selectedAcademyIds.includes(a.id) ? 'bg-primary/10 text-primary font-semibold' : 'hover:bg-muted'}`} data-testid={`announce-academy-${a.custom_id || a.id}`}>
                        <span className="flex items-center gap-2 truncate">
                          <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="truncate">{a.name}</span>
                          {a.city && <span className="text-muted-foreground">· {a.city}</span>}
                          {a.custom_id && <span className="text-muted-foreground font-mono">({a.custom_id})</span>}
                        </span>
                        {selectedAcademyIds.includes(a.id) && <CheckCircle2 className="h-4 w-4 flex-shrink-0" />}
                      </button>
                    ))}
                  </ScrollArea>
                </TabsContent>

                {/* City multi-select */}
                <TabsContent value="cities" className="mt-3 space-y-2">
                  {geoOptions.cities.length === 0 ? (
                    <div className="rounded-md border-2 border-dashed p-6 text-center text-xs text-muted-foreground" data-testid="announce-no-cities">
                      No academies have a city set yet. Set <code className="bg-muted px-1 rounded">city</code> on each academy from the Academies page, or use the Academy / All tabs instead.
                    </div>
                  ) : (
                    <ScrollArea className="h-40 rounded-md border p-2">
                      {geoOptions.cities.map(c => (
                        <button key={c.value} onClick={() => toggleCity(c.value)} className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-xs transition-colors duration-150 ${selectedCities.includes(c.value) ? 'bg-primary/10 text-primary font-semibold' : 'hover:bg-muted'}`} data-testid={`announce-city-${c.value}`}>
                          <span className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> {c.value}</span>
                          <span className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] py-0">{c.count} academy{c.count === 1 ? '' : 'ies'}</Badge>
                            {selectedCities.includes(c.value) && <CheckCircle2 className="h-4 w-4" />}
                          </span>
                        </button>
                      ))}
                    </ScrollArea>
                  )}
                </TabsContent>

                {/* State multi-select */}
                <TabsContent value="states" className="mt-3 space-y-2">
                  {geoOptions.states.length === 0 ? (
                    <div className="rounded-md border-2 border-dashed p-6 text-center text-xs text-muted-foreground" data-testid="announce-no-states">
                      No academies have a state set yet. Use <code className="bg-muted px-1 rounded">PATCH /api/super-admin/academies/{`{id}`}</code> with body <code className="bg-muted px-1 rounded">{`{"state":"Andhra Pradesh"}`}</code> to populate, or use other tabs.
                    </div>
                  ) : (
                    <ScrollArea className="h-40 rounded-md border p-2">
                      {geoOptions.states.map(s => (
                        <button key={s.value} onClick={() => toggleState(s.value)} className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-xs transition-colors duration-150 ${selectedStates.includes(s.value) ? 'bg-primary/10 text-primary font-semibold' : 'hover:bg-muted'}`} data-testid={`announce-state-${s.value}`}>
                          <span className="flex items-center gap-2"><Map className="h-3.5 w-3.5" /> {s.value}</span>
                          <span className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] py-0">{s.count} academy{s.count === 1 ? '' : 'ies'}</Badge>
                            {selectedStates.includes(s.value) && <CheckCircle2 className="h-4 w-4" />}
                          </span>
                        </button>
                      ))}
                    </ScrollArea>
                  )}
                </TabsContent>

                {/* All */}
                <TabsContent value="all" className="mt-3">
                  <div className="rounded-md border-2 border-dashed border-primary/30 bg-primary/5 p-5 text-center" data-testid="announce-mode-all-confirm">
                    <Globe className="h-8 w-8 mx-auto text-primary mb-2" />
                    <p className="text-sm font-semibold">Platform-wide broadcast</p>
                    <p className="text-xs text-muted-foreground mt-1">This will reach <strong>every</strong> academy on the platform ({geoOptions.total_academies} total).</p>
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            {/* Target Role + Severity */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold">2. Target Role</Label>
                <Select value={targetRole} onValueChange={setTargetRole}>
                  <SelectTrigger className="h-9 text-sm" data-testid="announce-target-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{TARGET_ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold">3. Severity</Label>
                <Select value={severity} onValueChange={setSeverity}>
                  <SelectTrigger className="h-9 text-sm" data-testid="announce-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(SEVERITY_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {/* Phase 72 — Multi-channel toggles */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold">4. Delivery Channels</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className={`flex items-center justify-between gap-2 p-3 rounded-lg border-2 transition-colors duration-150 ${channelInApp ? 'border-primary/40 bg-primary/5' : 'border-border bg-background'}`}>
                  <div className="flex items-center gap-2">
                    <Bell className="h-4 w-4 text-primary" />
                    <div>
                      <p className="text-xs font-medium">In-App Bell</p>
                      <p className="text-[10px] text-muted-foreground">Notification center</p>
                    </div>
                  </div>
                  <Switch checked={channelInApp} onCheckedChange={setChannelInApp} data-testid="announce-channel-inapp" />
                </div>
                <div className={`flex items-center justify-between gap-2 p-3 rounded-lg border-2 transition-colors duration-150 ${channelWhatsapp ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20' : 'border-border bg-background'}`}>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-emerald-600" />
                    <div>
                      <p className="text-xs font-medium">WhatsApp Relay</p>
                      <p className="text-[10px] text-muted-foreground">via Out-Play Twilio</p>
                    </div>
                  </div>
                  <Switch checked={channelWhatsapp} onCheckedChange={setChannelWhatsapp} data-testid="announce-channel-whatsapp" />
                </div>
              </div>
              {!hasValidChannel && <p className="text-[11px] text-red-600">At least one delivery channel must be enabled.</p>}
            </div>

            {/* Title + Message */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold">5. Title <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Monsoon Safety Advisory" className="h-9 text-sm" maxLength={120} data-testid="announce-title" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <Label className="text-xs font-semibold">6. Message</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={aiTopicHint}
                    onChange={(e) => setAiTopicHint(e.target.value)}
                    placeholder="Optional topic hint (e.g. monsoon advisory, new payment dashboard)"
                    className="h-8 text-[11px] w-[280px]"
                    maxLength={240}
                    data-testid="announce-ai-topic-hint"
                  />
                  <Button
                    type="button" variant="outline" size="sm"
                    disabled={aiDrafting}
                    onClick={handleAiDraft}
                    className="h-8 text-[11px] gap-1.5 border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100"
                    data-testid="announce-ai-copilot-btn"
                  >
                    {aiDrafting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                    {aiDrafting ? 'Drafting…' : 'AI Copilot draft'}
                  </Button>
                </div>
              </div>
              <textarea
                value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder="Heavy rain is forecast over Andhra Pradesh tomorrow. Please ensure outdoor practice is suspended if conditions deteriorate."
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[110px]"
                maxLength={2000} data-testid="announce-message"
              />
              <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                <span>
                  {aiSource === 'llm' && (
                    <span className="inline-flex items-center gap-1 text-purple-700 font-semibold">
                      <Sparkles className="h-3 w-3" /> Drafted by AI Copilot — edit before sending.
                    </span>
                  )}
                  {aiSource === 'fallback_template' && (
                    <span className="text-amber-700">Drafted from on-device template (LLM unavailable).</span>
                  )}
                </span>
                <span>{message.length} / 2000 · Auto-appends "— Out-Play Super Admin" footer</span>
              </div>
            </div>

            {/* Preview */}
            {message.trim() && (
              <div className="rounded-lg border-2 border-dashed bg-muted/30 p-4" data-testid="announce-preview">
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Preview (what recipients will see)</span>
                </div>
                <div className={`rounded-md p-3 ${SEVERITY_META[severity].color} border`}>
                  <div className="flex items-start gap-2">
                    <SevIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      {title && <p className="font-semibold text-sm">{title}</p>}
                      <p className="text-xs leading-relaxed mt-1 whitespace-pre-wrap">{message}</p>
                      <p className="text-[10px] mt-2 opacity-70">— Out-Play Super Admin</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Submit */}
            <Button onClick={handleSubmit} disabled={!canSubmit || submitting} className="w-full h-10" data-testid="announce-submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              {submitting ? 'Broadcasting…' : `Broadcast to ${selectionLabel}`}
            </Button>
          </CardContent>
        </Card>

        {/* History panel + Phone preview (1/3 width) */}
        <div className="space-y-6">
        {/* Live phone-frame preview */}
        <Card data-testid="announce-phone-preview-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Smartphone className="h-4 w-4 text-primary" /> Live Phone Preview
            </CardTitle>
            <CardDescription className="text-xs">
              Real-time render of how the in-app bell + WhatsApp body will look.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              className="mx-auto rounded-[36px] border-[8px] border-slate-900 bg-slate-900 p-2 shadow-xl w-[240px]"
              data-testid="announce-phone-frame"
            >
              <div className="mx-auto h-3.5 w-20 rounded-b-2xl bg-slate-900 mb-1" />
              <div className="bg-white rounded-[24px] overflow-hidden">
                {/* Status bar */}
                <div className="flex items-center justify-between px-3 pt-2 pb-1 text-[9px] font-bold text-slate-800">
                  <span>9:41</span>
                  <span>•••</span>
                </div>
                {/* Notification card mock */}
                <div className="px-2 pb-3">
                  <div className={`rounded-xl px-2.5 py-2 border ${SEVERITY_META[severity].color}`}>
                    <div className="flex items-center gap-1.5">
                      <div className="h-5 w-5 rounded-md bg-emerald-500 text-white text-[8px] font-extrabold flex items-center justify-center">OP</div>
                      <span className="text-[10px] font-extrabold text-slate-900">Out-Play</span>
                      <span className="text-[8px] text-slate-500 ml-auto">now</span>
                    </div>
                    <div className="mt-1.5">
                      <p className="text-[11px] font-bold leading-tight truncate">
                        {title || 'Your broadcast title preview'}
                      </p>
                      <p className="text-[10px] leading-snug mt-0.5 line-clamp-5 whitespace-pre-wrap">
                        {message || 'Type a message in the composer or hit the AI Copilot to see a live preview here.'}
                      </p>
                      <p className="text-[8px] mt-1 opacity-70">— Out-Play Super Admin</p>
                    </div>
                  </div>

                  {/* WhatsApp mini-bubble — only if channel toggled */}
                  {channelWhatsapp && (
                    <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-xl px-2.5 py-1.5">
                      <div className="flex items-center gap-1.5">
                        <Phone className="h-2.5 w-2.5 text-emerald-700" />
                        <span className="text-[9px] font-bold text-emerald-800">WhatsApp</span>
                      </div>
                      <p className="text-[10px] text-slate-800 mt-1 line-clamp-3 whitespace-pre-wrap">
                        {message || 'WhatsApp-relay preview appears as you type.'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="mx-auto h-1 w-12 rounded-full bg-slate-700 mt-1" />
            </div>

            {/* Predictive reach footer */}
            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-1.5"
              data-testid="announce-reach-footer">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Predictive reach</span>
                {reachLoading && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
              </div>
              {reach ? (
                <>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-extrabold text-slate-900 tabular-nums">{reach.users}</span>
                    <span className="text-[10px] text-slate-500">
                      {reach.users === 1 ? 'verified recipient' : 'verified recipients'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-slate-500">
                    <span>{reach.academies} {reach.academies === 1 ? 'academy' : 'academies'} in scope</span>
                    <span className="capitalize">{reach.scope}</span>
                  </div>
                </>
              ) : (
                <p className="text-[11px] text-slate-400">Pick a target above to see the live recipient count.</p>
              )}
            </div>

            {/* Phase 168 telemetry strip — last 7d open/click */}
            {telemetry?.by_type?.length > 0 && (
              <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3 space-y-2"
                data-testid="announce-telemetry-strip">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Email telemetry · {telemetry.window_days}d
                  </span>
                  <span className="text-[9px] text-slate-400">Phase 168</span>
                </div>
                <div className="space-y-1.5">
                  {telemetry.by_type.slice(0, 3).map(t => (
                    <div key={t.log_type} className="flex items-center justify-between text-[10px]">
                      <span className="font-semibold text-slate-700 truncate max-w-[120px]">{t.log_type}</span>
                      <span className="text-slate-500 flex items-center gap-2 tabular-nums">
                        <span title="sent" className="font-bold text-slate-900">{t.sent}</span>
                        <span className="text-emerald-700 flex items-center gap-0.5" title="open rate">
                          <MailOpen className="h-2.5 w-2.5" />{t.open_rate}%
                        </span>
                        <span className="text-blue-700 flex items-center gap-0.5" title="click rate">
                          <MousePointerClick className="h-2.5 w-2.5" />{t.click_rate}%
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
                {telemetry.by_type.every(t => t.open_rate === 0) && (
                  <p className="text-[9px] text-slate-400 leading-tight">
                    Open/click counts populate once Resend webhooks fire to <code className="text-[8px]">/api/webhooks/resend</code>.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Broadcasts (history) */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><History className="h-4 w-4 text-primary" /> Recent Broadcasts</CardTitle>
            <CardDescription>Last 30 broadcasts · delete retracts notifications.</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[640px]">
              {loadingHistory ? <div className="text-center text-xs text-muted-foreground py-6">Loading…</div>
              : history.length === 0 ? <div className="text-center text-xs text-muted-foreground py-12">No broadcasts yet.<br />Compose your first one →</div>
              : <div className="space-y-3" data-testid="announce-history-list">
                  {history.map(h => {
                    const meta = SEVERITY_META[h.severity] || SEVERITY_META.info;
                    const Icon = meta.icon;
                    const channelLabels = (h.channels || ['inapp']).join(' + ');
                    return (
                      <div key={h.id} className="border rounded-lg p-3 space-y-2 hover:border-primary/40 transition-colors duration-150" data-testid={`announce-history-${h.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Badge className={`${meta.color} text-[10px] py-0`} variant="outline"><Icon className="h-2.5 w-2.5 mr-1" />{meta.label}</Badge>
                            {h.target_mode && h.target_mode !== 'academies' && <Badge variant="secondary" className="text-[10px] py-0 capitalize">{h.target_mode}</Badge>}
                          </div>
                          <button onClick={() => handleDelete(h.id)} className="text-muted-foreground hover:text-destructive" data-testid={`announce-delete-${h.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                        {h.title && <p className="text-xs font-semibold truncate">{h.title}</p>}
                        <p className="text-[11px] text-muted-foreground line-clamp-2">{h.message}</p>
                        <div className="flex items-center justify-between text-[10px] text-muted-foreground pt-1 border-t">
                          <span>{h.total_recipients} recip · {h.total_academies} acad · {channelLabels}</span>
                          <span className="font-mono">{(h.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              }
            </ScrollArea>
          </CardContent>
        </Card>
        </div>
      </div>

      {/* Delivery report dialog */}
      <Dialog open={!!resultDialog} onOpenChange={(o) => !o && setResultDialog(null)}>
        <DialogContent className="max-w-md" data-testid="announce-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> Delivery Report</DialogTitle>
          </DialogHeader>
          {resultDialog && (
            <div className="space-y-3">
              <p className="text-sm">{resultDialog.message}</p>
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-700">{resultDialog.total_recipients}</p>
                  <p className="text-[10px] text-emerald-700/80 uppercase tracking-wide">In-App</p>
                </div>
                <div className="rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 p-3 text-center">
                  <p className="text-2xl font-bold text-blue-700">{resultDialog.total_academies}</p>
                  <p className="text-[10px] text-blue-700/80 uppercase tracking-wide">Academies</p>
                </div>
                <div className="rounded-lg bg-orange-50 dark:bg-orange-950/20 border border-orange-200 p-3 text-center">
                  <p className="text-2xl font-bold text-orange-700">{resultDialog.total_whatsapp_sent || 0}</p>
                  <p className="text-[10px] text-orange-700/80 uppercase tracking-wide">WhatsApp</p>
                </div>
              </div>
              {resultDialog.total_whatsapp_failed > 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 rounded p-2">{resultDialog.total_whatsapp_failed} WhatsApp send{resultDialog.total_whatsapp_failed === 1 ? '' : 's'} failed — see Delivery Logs for details.</p>
              )}
              <ScrollArea className="h-40 rounded-md border">
                <table className="w-full text-xs">
                  <thead className="bg-muted sticky top-0">
                    <tr><th className="text-left px-3 py-1.5">Academy</th><th className="text-right px-3 py-1.5">In-App</th><th className="text-right px-3 py-1.5">WA</th></tr>
                  </thead>
                  <tbody>
                    {(resultDialog.per_academy || []).map(p => (
                      <tr key={p.academy_id} className="border-b">
                        <td className="px-3 py-1.5 truncate">{p.name} {p.custom_id && <span className="text-muted-foreground font-mono">({p.custom_id})</span>}{p.city && <span className="text-muted-foreground"> · {p.city}</span>}</td>
                        <td className="px-3 py-1.5 text-right font-semibold tabular-nums">{p.sent}</td>
                        <td className="px-3 py-1.5 text-right font-semibold tabular-nums text-emerald-700">{p.whatsapp_sent || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
              <Button onClick={() => setResultDialog(null)} className="w-full"><X className="h-4 w-4 mr-1" /> Close</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
